import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (password !== confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
      toast.success('Contraseña actualizada correctamente');
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'El enlace es inválido o ha caducado');
    }
    setSubmitting(false);
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center overflow-y-auto"
      style={{
        backgroundImage: 'url(/login-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Velo berenjena */}
      <div className="fixed inset-0 pointer-events-none" style={{ backgroundColor: 'rgba(72,41,58,0.42)', zIndex: 0 }} />

      <div className="relative w-full max-w-md px-4" style={{ zIndex: 10 }}>
        <div className="flex justify-center mb-6">
          <img src="/logo.png" alt="El Club de Manri" style={{ width: 'min(70vw, 320px)', height: 'auto' }} className="drop-shadow-2xl" />
        </div>

        <div className="rounded-3xl shadow-2xl p-8" style={{ backgroundColor: 'rgba(251,248,243,0.97)' }}>
          {!token ? (
            <div className="text-center space-y-3">
              <p className="font-heading font-bold text-xl text-[#48293A]">Enlace no válido</p>
              <p className="text-[#4C4D57] text-sm">Este enlace de recuperación no es válido.</p>
              <button onClick={() => navigate('/')} className="btn-manrique text-sm">Ir al inicio</button>
            </div>
          ) : done ? (
            <div className="text-center space-y-3" data-testid="reset-success">
              <div className="w-14 h-14 rounded-full bg-[#27AE60]/10 flex items-center justify-center mx-auto">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <p className="font-heading font-bold text-xl text-[#48293A]">Contraseña actualizada</p>
              <p className="text-[#4C4D57] text-sm">Redirigiendo al inicio de sesión…</p>
            </div>
          ) : (
            <>
              <h1 className="font-heading font-bold text-2xl tracking-tight text-[#48293A] mb-1">
                Nueva contraseña
              </h1>
              <p className="text-[#4C4D57] text-sm mb-6">
                Elige una contraseña segura de al menos 8 caracteres.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#48293A] mb-1.5">Nueva contraseña</label>
                  <div className="relative">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="input-manrique pr-10"
                      required
                      minLength={8}
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#48293A]"
                    >
                      {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  {password.length > 0 && password.length < 8 && (
                    <p className="text-xs text-red-500 mt-1">Mínimo 8 caracteres</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#48293A] mb-1.5">Confirmar contraseña</label>
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className="input-manrique"
                    required
                    data-testid="reset-confirm-input"
                  />
                  {confirm.length > 0 && confirm !== password && (
                    <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-manrique w-full flex items-center justify-center gap-2 mt-2"
                  data-testid="reset-submit-button"
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>Guardar contraseña <ArrowRight size={18} /></>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
